import {
  SearchState,
  SearchGettersTree,
  SearchMutationsTree,
  SearchTab,
  SearchActionsTree
} from '@web/store/types/modules/search'
import { IPagination } from '@web/store/types/modules/shortCourse'
import ShortCourseApi from '@web/api/modules/ShortCourse'
import ClubMaterialsApi from '@web/api/modules/ClubMaterials'
import { analyticsSearchSubmit, analyticsSearchError } from '@web/services/Analytics/events/searchEvents'
import SearchQueriesWebhook from '@web/api/modules/SearchQueriesWebhook'

// По макету — 6 карточек на страницу выдачи (2 ряда по 3 на десктопе).
const DEFAULT_PER_PAGE = 6

/**
 * MOB-469: поиск по материалам клуба временно скрыт — в выдаче остаются
 * только курсы. Гейтит и запрос к клубному источнику, и клубный UI (ряд
 * табов, секция «Материалы клуба» — см. views.v2/Search.vue). Вернуть
 * клубный поиск — поставить true, других правок не требуется.
 */
export const IS_CLUB_SEARCH_ENABLED = false

function emptyPagination (): IPagination {
  return { currentPage: 1, pageCount: 0, perPage: DEFAULT_PER_PAGE, totalCount: 0 }
}

function createInitialState (): SearchState {
  return {
    query: '',
    activeTab: 'all',
    courses: [],
    coursesPagination: emptyPagination(),
    clubMaterials: [],
    clubPagination: emptyPagination(),
    isSearching: false,
    error: null,
    hasSearched: false,
    requestId: 0
  }
}

const state: SearchState = createInitialState()

const getters: SearchGettersTree = {
  getQuery: (state) => state.query,
  getActiveTab: (state) => state.activeTab,
  getCourses: (state) => state.courses,
  getClub: (state) => state.clubMaterials,
  getCoursesPagination: (state) => state.coursesPagination,
  getClubPagination: (state) => state.clubPagination,
  getIsSearching: (state) => state.isSearching,
  getError: (state) => state.error,
  getHasSearched: (state) => state.hasSearched
}

const mutations: SearchMutationsTree = {
  setQuery (state, payload: string) { state.query = payload },
  setActiveTab (state, payload: SearchTab) { state.activeTab = payload },
  setCourses (state, payload) { state.courses = payload },
  appendCourses (state, payload) { state.courses = [...state.courses, ...payload] },
  setCoursesPagination (state, payload) { state.coursesPagination = payload },
  setClub (state, payload) { state.clubMaterials = payload },
  appendClub (state, payload) { state.clubMaterials = [...state.clubMaterials, ...payload] },
  setClubPagination (state, payload) { state.clubPagination = payload },
  setIsSearching (state, payload: boolean) { state.isSearching = payload },
  setError (state, payload: string | null) { state.error = payload },
  setHasSearched (state, payload: boolean) { state.hasSearched = payload },
  incrementRequestId (state) { state.requestId += 1 },
  reset (state) {
    // requestId не обнуляем (чтобы ответ уже отменённого запроса не стал
    // «актуальным» задним числом), а инкрементируем: летящий актуальный
    // запрос после reset должен стать устаревшим и быть отброшен — иначе
    // он допишет данные в только что очищенный стор (см. Task 6+, где
    // reset вызывается при уходе со страницы поиска).
    const nextRequestId = state.requestId + 1
    Object.assign(state, createInitialState(), { requestId: nextRequestId })
  },
  logout (state) {
    // При логауте защита от гонки неактуальна, состояние обнуляется полностью.
    Object.assign(state, createInitialState())
  }
}

function intHeader (headers: Headers, name: string, fallback: number): number {
  const raw = headers.get(name)
  if (!raw) return fallback
  const n = parseInt(raw, 10)
  return Number.isFinite(n) ? n : fallback
}

function toPagination (headers: Headers): IPagination {
  const currentPage = intHeader(headers, 'x-pagination-current-page', 1)
  const perPage = intHeader(headers, 'x-pagination-per-page', DEFAULT_PER_PAGE)
  const totalCount = intHeader(headers, 'x-pagination-total-count', 0)

  // pageCount берём из хедера, но если его нет — считаем сами из totalCount.
  // Иначе при отсутствующем заголовке (бэкенд его не шлёт, либо CORS не
  // отдаёт кастомные заголовки) pageCount = 0, и кнопка «Показать еще» не
  // появляется никогда, даже когда результатов заведомо больше страницы.
  const headerPageCount = intHeader(headers, 'x-pagination-page-count', 0)
  const derivedPageCount = perPage > 0 ? Math.ceil(totalCount / perPage) : 0

  return {
    currentPage,
    pageCount: headerPageCount || derivedPageCount,
    perPage,
    totalCount
  }
}

const actions: SearchActionsTree = {
  async submitSearch ({ state, commit, rootGetters }, { query }) {
    commit('incrementRequestId')
    const myRequestId = state.requestId

    commit('setQuery', query)
    commit('setIsSearching', true)
    commit('setError', null)

    // allSettled, а не all: падение клуба (реальный клубный бэкенд ещё не
    // подключён — эпик SEARCH-HOME, открытый вопрос E) не должно убивать
    // уже пришедшую выдачу курсов и портить no-result rate ложным
    // search_error. Каждый источник деградирует независимо; error и
    // search_error — только если оба источника упали.
    const [coursesResult, clubResult] = await Promise.allSettled([
      ShortCourseApi.search({ textSearch: query, page: 1, perPage: DEFAULT_PER_PAGE }),
      // MOB-469: клубный источник скрыт — запрос не отправляем, но форму
      // результата сохраняем (null вместо ответа), чтобы ветка разбора ниже
      // не переписывалась и ожила от одного переключения константы.
      IS_CLUB_SEARCH_ENABLED
        ? ClubMaterialsApi.search({ textSearch: query, page: 1, perPage: DEFAULT_PER_PAGE })
        : Promise.resolve(null)
    ])

    // Устаревший ответ: пришёл новый сабмит, пока летел этот. Отбрасываем
    // целиком — не пишем данные, не шлём аналитику.
    if (state.requestId !== myRequestId) return

    // Сырой запрос — в n8n, параллельно аналитике. Здесь, а не в ветках ниже:
    // текст запроса ценен независимо от того, ответил ли бэкенд успехом или
    // ошибкой. Устаревшие запросы сюда не доходят — отсечены guard'ом выше.
    // Отправка не блокирует выдачу и не может её уронить (см. модуль).
    SearchQueriesWebhook.send({
      userId: rootGetters['user/user']?.id ?? '',
      email: rootGetters['user/email'] ?? '',
      query
    })

    // Курсы — источник истины для no-result rate: их падение всегда техническая
    // ошибка, а не «ничего не найдено», независимо от судьбы клуба (клуб пока
    // заглушка на Promise.resolve и никогда не падает сам по себе — оживёт
    // вместе с реальным клубным бэкендом, эпик SEARCH-HOME, открытый вопрос E).
    // Падение только клуба при живых курсах — по-прежнему обычный submit.
    if (coursesResult.status === 'rejected') {
      commit('setCourses', [])
      commit('setClub', [])
      const error = coursesResult.reason
      commit('setError', error instanceof Error ? error.message : 'Ошибка поиска')
      commit('setHasSearched', true)
      commit('setIsSearching', false)

      analyticsSearchError({ query })
      return
    }

    commit('setCourses', coursesResult.value.data.courses)
    commit('setCoursesPagination', toPagination(coursesResult.value.headers))

    // value === null — клубный источник выключен константой: не трогаем
    // клубное состояние вообще, чтобы в нём не осталось следов скрытого
    // раздела (пагинация остаётся начальной).
    if (clubResult.status === 'fulfilled' && clubResult.value !== null) {
      commit('setClub', clubResult.value.data)
      commit('setClubPagination', toPagination(clubResult.value.headers))
    } else if (clubResult.status === 'rejected') {
      commit('setClub', [])
    }

    commit('setHasSearched', true)
    commit('setIsSearching', false)

    analyticsSearchSubmit({
      query,
      hasResults: coursesResult.value.data.courses.length > 0,
      coursesCount: intHeader(coursesResult.value.headers, 'x-pagination-total-count', coursesResult.value.data.courses.length)
    })
  },

  async loadMoreCourses ({ state, commit }) {
    const nextPage = state.coursesPagination.currentPage + 1
    if (nextPage > state.coursesPagination.pageCount) return

    const response = await ShortCourseApi.search({
      textSearch: state.query,
      page: nextPage,
      perPage: state.coursesPagination.perPage
    })

    commit('appendCourses', response.data.courses)
    commit('setCoursesPagination', toPagination(response.headers))
  },

  async loadMoreClub ({ state, commit }) {
    // MOB-469: клубный источник скрыт — догружать нечего.
    if (!IS_CLUB_SEARCH_ENABLED) return

    const nextPage = state.clubPagination.currentPage + 1
    if (nextPage > state.clubPagination.pageCount) return

    const response = await ClubMaterialsApi.search({
      textSearch: state.query,
      page: nextPage,
      perPage: state.clubPagination.perPage
    })

    commit('appendClub', response.data)
    commit('setClubPagination', toPagination(response.headers))
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
// eslint-disable-next-line @typescript-eslint/no-explicit-any
} as any
